<script setup lang="ts">
import { format } from 'date-fns'

/* Stores */
import { useBasketStore } from '@/store/basket/basket'
import { useAppStore } from '@/store/app/app'
import { useCityStore } from '@/store/city/city'

/* Components */
import BasketProposition from '@/components/Basket/basket-proposition/BasketProposition.vue'
import IconArrowLeft from '@/components/Icons/IconArrowLeft.vue'
import IconArrowRight from '@/components/Icons/IconArrowRight.vue'

/* Types */
import { PropositionItem } from '@/api/modules/basket/types'
import { SnackbarType } from '@/store/app/app.types'

const props = defineProps({
  propositions: {
    type: Array as PropType<PropositionItem[]>,
    required: true,
  },
  timeToLive: {
    type: Number,
    default: 0,
  },
})

const emit = defineEmits(['close'])

const cityStore = useCityStore()
const basketStore = useBasketStore()
const { showSnackbar } = useAppStore()

const { isMobile } = useDevice()

const { $sentryCaptureException, $metrics } = useNuxtApp()

const basket = computed(() => basketStore.data)
const currencySymbol = computed(() => cityStore.currencySymbol)

const selectedProposition = ref<PropositionItem | null>(null)

const amountToPayWithSymbol = computed(
  () => `${basket.value.amountToPay} ${currencySymbol.value}`,
)

const deleteSelectedProposition = async () => {
  if (!selectedProposition.value) return

  await basketStore.setProposition({
    proposition_item_id: selectedProposition.value.id,
    quantity: 0,
  })
}

const selectProposition = async (proposition: PropositionItem) => {
  try {
    isLoading.value = true

    // Удаляем текущую позицию добавок
    await deleteSelectedProposition()

    await basketStore.setProposition({
      proposition_item_id: proposition.id,
    })

    selectedProposition.value = proposition

    isLoading.value = false
  } catch (error) {
    selectedProposition.value = null
    $sentryCaptureException(error)

    if (error.response?.status === 422) {
      showSnackbar({
        message: 'Что-то пошло не так',
        type: SnackbarType.MESSAGE,
      })
    }
  }
}

let timerIntervalId = null as NodeJS.Timeout | null
const countdownSeconds = ref(0)

const countdownFormatter = computed(() => {
  const minutesCount = format(
    new Date(0, 0, 0, 0, 0, countdownSeconds.value),
    'mm',
  )
  const secondsCount = format(
    new Date(0, 0, 0, 0, 0, countdownSeconds.value),
    'ss',
  )

  return `${minutesCount} : ${secondsCount}`
})

const startTimer = (timerInitial: number) => {
  countdownSeconds.value = timerInitial

  return setInterval(() => {
    countdownSeconds.value--

    // Если еще таймер идет
    if (countdownSeconds.value !== 0) return

    if (timerIntervalId) clearInterval(timerIntervalId)

    timerEndHandler()
  }, 1000)
}

const timerEndHandler = async () => {
  await deleteSelectedProposition()

  await closeDialog()
}

const beforeWindowUnload = (event: Event) => {
  event.preventDefault()

  return 'Данные не сохранены. Точно закрыть?'
}

onMounted(() => {
  // Прослушивание события закрытия вкладки/окна
  window.addEventListener('beforeunload', beforeWindowUnload)

  if (timerIntervalId) clearInterval(timerIntervalId)

  if (props.timeToLive === 0) {
    timerEndHandler()
    return
  }

  timerIntervalId = startTimer(props.timeToLive)

  $metrics?.eventSendGoal({ ym: 'PROPOSITION_SHOWN' })
})

onBeforeUnmount(() => {
  // удаляем событие оповощения о закрытии вкладки/окна
  window.removeEventListener('beforeunload', beforeWindowUnload)
  if (timerIntervalId) clearInterval(timerIntervalId)
})

const closeDialog = async (
  isError: boolean = false,
  withoutProposition?: boolean,
) => {
  if (withoutProposition) await deleteSelectedProposition()

  emit('close', isError)
}

const isLoading = ref(false)

const onSubmit = async (withProposition: boolean) => {
  // С добавками
  if (withProposition) {
    await closeDialog()

    return
  }

  try {
    isLoading.value = true

    if (selectedProposition.value) {
      await basketStore.setProposition({
        proposition_item_id: selectedProposition.value.id,
        quantity: 0,
      })
    }

    $metrics?.eventSendGoal({ ym: 'PROPOSITION_ADD_TO_CART' })

    await closeDialog()

    isLoading.value = false
  } catch (error) {
    selectedProposition.value = null
    $sentryCaptureException(error)

    if (error.response?.status === 422) {
      showSnackbar({
        message: 'Что-то пошло не так',
        type: SnackbarType.MESSAGE,
      })
    }

    closeDialog(true)
  }
}
</script>

<template>
  <app-dialog
    :model-value="true"
    :hide-header="true"
    :fullscreen="isMobile"
    max-width="750px"
    width="100%"
    v-bind="$attrs"
    scroll-strategy="none"
  >
    <template #body>
      <div
        class="propositions-dialog"
        :class="{ 'propositions-dialog--mobile': isMobile }"
      >
        <div class="propositions-dialog__header">
          <div class="close-btn">
            <app-button
              variant="text"
              :disabled="isLoading"
              @click="closeDialog(false, true)"
            >
              <span class="close-btn__text">Закрыть и оформить</span>

              <template #append>
                <app-icon name="close" fill-color="#fff" />
              </template>
            </app-button>
          </div>

          <div class="timer">
            <div class="timer__container">
              <div class="timer__title">Скидка для вас</div>

              <div>
                Закончится через
                <span class="timer__time">{{ countdownFormatter }}</span>
              </div>
            </div>

            <div class="timer__alarm-image">
              <nuxt-img
                src="./basket-fire-alarm.png"
                alt="Картинка будильника"
              />
            </div>
          </div>

          <div class="placeholder">Можно выбрать только одно блюдо</div>
        </div>

        <div class="propositions-list">
          <v-slide-group
            v-if="!isMobile"
            show-arrows
            :prev-icon="IconArrowLeft"
            :next-icon="IconArrowRight"
          >
            <v-slide-group-item
              v-for="proposition in props.propositions"
              :key="proposition.id"
              :value="proposition"
            >
              <basket-proposition
                :proposition="proposition"
                :is-selected="selectedProposition?.id === proposition.id"
                @set-proposition="selectProposition"
              />
            </v-slide-group-item>
          </v-slide-group>

          <div v-else class="propositions-container">
            <div
              v-for="proposition in props.propositions"
              :key="proposition.id"
              class="propositions-container__item"
            >
              <basket-proposition
                :proposition="proposition"
                :is-selected="selectedProposition?.id === proposition.id"
                @set-proposition="selectProposition"
              />
            </div>
          </div>
        </div>

        <div class="actions">
          <transition-group>
            <app-button
              class="actions__btn actions__btn--gray"
              :disabled="isLoading"
              @click="onSubmit(false)"
            >
              Оформить без предложений
            </app-button>

            <app-button
              v-if="selectedProposition"
              class="actions__btn"
              :loading="isLoading"
              @click="onSubmit(true)"
            >
              Оформить заказ
              <span v-if="amountToPayWithSymbol" class="actions__amount">
                {{ amountToPayWithSymbol }}
              </span>
            </app-button>
          </transition-group>
        </div>
      </div>
    </template>
  </app-dialog>
</template>

<style lang="scss" scoped>
.close-btn {
  width: max-content;
  margin-left: auto;

  .app-button {
    color: $white;
  }

  &__text {
    margin-right: 8px;
    font-weight: 600;
    color: $white;
  }

  .app-button.v-btn--disabled {
    color: $white !important;
    background-color: transparent !important;
  }
}

.timer {
  display: flex;
  justify-content: space-between;

  &__container {
    margin-top: 23px;
    font-size: 14px;
    font-weight: 600;
    line-height: 120%;
    color: $white;
  }

  &__title {
    margin-bottom: 14px;
    font-size: 20px;
    font-weight: 800;
    line-height: 22px;
  }

  &__time {
    margin-left: 8px;
    font-size: 20px;
    font-weight: 600;
    line-height: 24px;
  }

  &__alarm-image {
    position: relative;
    padding-right: 30px;

    &:before {
      position: absolute;
      top: 50%;
      left: 40px;
      display: block;
      width: 67px;
      height: 67px;
      content: '';
      background: #fff;
      filter: blur(40px);
      transform: translate(-50%);
      transform: translateY(-50%);
    }

    img {
      position: relative;
      z-index: 1;
      width: 178px;
      height: 134px;
    }
  }
}

.placeholder {
  margin-top: -20px;
  font-size: 14px;
  font-weight: 600;
  color: $white;
  opacity: 0.6;
}

.actions {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 64px auto 30px;

  &__btn {
    width: 260px;
    height: 50px;
    font-weight: 800;

    &--gray {
      color: $text-dark-gray !important;
      background: #f5f6f7 !important;
      box-shadow: none !important;

      &.app-button.v-btn--disabled {
        color: $text-dark-gray !important;
        background: #f5f6f7 !important;
      }
    }
  }

  &__amount {
    margin-left: 4px;
  }
}

.propositions-list {
  position: relative;
  padding: 0 24px;

  :deep(.v-slide-group__content) {
    gap: 16px;
    padding: 26px 0;
  }

  :deep(.v-slide-group__prev),
  :deep(.v-slide-group__next) {
    position: absolute;
    top: 0;
    z-index: 1;
    height: 100%;

    .v-icon {
      width: 40px;
      height: 40px;
      background-color: #fff;
      border-radius: 50%;
      box-shadow: 0 4px 20px rgba(65, 58, 91, 0.15);
    }
  }

  :deep(.v-slide-group__next) {
    right: 0;
  }

  :deep(.v-slide-group__prev) {
    left: 0;
  }

  :deep(.v-slide-group__prev--disabled),
  :deep(.v-slide-group__next--disabled) {
    display: none;
  }
}

.propositions-container {
  display: flex;
  flex-wrap: nowrap;
  gap: 10px;
  padding: 24px 20px;
  margin: 0 -20px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-padding-inline: 20px;

  &::-webkit-scrollbar {
    display: none;
  }
}

.propositions-dialog {
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: $white;
  border-radius: 10px;

  &:before {
    position: absolute;
    width: 100%;
    height: 338px;
    content: '';
    background-color: #423d7e;
  }

  &__header {
    position: relative;
    padding: 20px 20px 0 30px;
  }

  &--mobile {
    height: 100%;

    .close-btn__text {
      font-size: 12px;
    }

    .propositions-dialog__header {
      padding: 20px 20px 0 20px;
    }

    .timer {
      &__container {
        margin-top: 19px;
      }

      &__alarm-image {
        padding-right: 0;
        margin-right: -10px;

        &:before {
          left: 24px;
          width: 48px;
          height: 48px;
        }

        img {
          width: 116px;
          height: 88px;
        }
      }
    }

    .placeholder {
      margin-top: 30px;
    }

    .propositions-list {
      padding: 0 20px;
    }

    .actions {
      width: 100%;
      padding: 0 20px;
      margin: auto auto 24px;

      &__btn {
        width: 100%;
      }
    }
  }
}
</style>
